/**
 * "Pick a size" sheet used by the mobile sticky bar (snippets/tabla-size.liquid).
 * Available sizes add straight to the bag (via cart-handler.js); sold-out sizes select the size
 * in the main picker and open the Klaviyo waitlist modal.
 */
class SizeChartModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.selectedColor = null;
    this.isProcessingSelection = false;
    this.returnFocusTo = null;
    /** @type {AbortController | undefined} */
    this.controller = undefined;
  }

  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener('click', this.handleClick, { signal });
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      },
      { signal }
    );
    document.addEventListener('open-size-chart', () => this.open(), { signal });
    document.addEventListener('color-selected', this.handleColorSelected, { signal });
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target instanceof Element ? event.target.closest('[data-size-chart]') : null;
        if (!link) return;
        event.preventDefault();
        this.open();
      },
      { signal }
    );

    window.openSizeChart = () => this.open();
    window.closeSizeChart = () => this.close();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('.size-chart-overlay, .size-chart-close')) {
      this.close();
      return;
    }

    // The size guide dialog (size-guide.js) opens on top; close the sheet underneath.
    if (target.closest('[data-size-guide-trigger]')) {
      this.close({ restoreFocus: false });
      return;
    }

    const row = target.closest('.size-chart-row[data-size-value]');
    if (row instanceof HTMLElement) this.handleSizeSelection(row);
  };

  open() {
    this.returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.style.display = 'block';
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    this.syncFromVariantPicker();

    const focusTarget = this.querySelector('.size-chart-row.selected') || this.querySelector('.size-chart-row, .size-chart-close');
    if (focusTarget instanceof HTMLElement) requestAnimationFrame(() => focusTarget.focus());

    this.dispatchEvent(new CustomEvent('size-chart-opened', { bubbles: true, detail: { modal: this } }));
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    this.style.display = 'none';
    document.body.style.overflow = '';
    this.isOpen = false;
    if (restoreFocus && this.returnFocusTo?.isConnected) this.returnFocusTo.focus();
    this.dispatchEvent(new CustomEvent('size-chart-closed', { bubbles: true, detail: { modal: this } }));
  }

  get mainPicker() {
    return document.querySelector('variant-picker[data-template-product-match]') || document.querySelector('variant-picker');
  }

  /** Current size value of the main picker, if any. */
  getSelectedSize() {
    const picker = this.mainPicker;
    if (!picker) return null;
    const input = picker.querySelector('[data-option-kind="size"] input:checked');
    if (input instanceof HTMLInputElement) return input.value;
    const select = picker.querySelector('[data-option-kind="size"] select');
    return select instanceof HTMLSelectElement ? select.value : null;
  }

  syncFromVariantPicker() {
    let currentSize = this.getSelectedSize();

    if (!currentSize) {
      const builderSelect = document.querySelector('pdp-bundle-builder .pdp-builder__size-select');
      if (builderSelect instanceof HTMLSelectElement && builderSelect.value) currentSize = builderSelect.value;
    }

    if (currentSize) this.highlightSize(currentSize);
  }

  /** @param {string} sizeValue */
  highlightSize(sizeValue) {
    this.querySelectorAll('.size-chart-row[data-size-value]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const match = row.dataset.sizeValue?.trim() === sizeValue.trim();
      row.classList.toggle('selected', match);
      if (match) row.setAttribute('aria-current', 'true');
      else row.removeAttribute('aria-current');
    });
  }

  /** @param {HTMLElement} row */
  handleSizeSelection(row) {
    if (this.isProcessingSelection) return;
    this.isProcessingSelection = true;
    window.setTimeout(() => {
      this.isProcessingSelection = false;
    }, 500);

    const sizeValue = row.dataset.sizeValue;
    if (!sizeValue) return;

    if (row.hasAttribute('data-waitlist-size')) {
      this.joinWaitlist(sizeValue);
      return;
    }

    this.syncSizeWithVariantPicker(sizeValue);

    document.dispatchEvent(
      new CustomEvent('add-to-cart', {
        bubbles: true,
        detail: {
          variantId: row.dataset.variantId || null,
          size: sizeValue,
          color: this.getSelectedColor(),
          source: 'size-chart-modal',
        },
      })
    );

    this.close();
  }

  /**
   * Selects the sold-out size in the main picker, waits for the variant update so the
   * main button turns into the waitlist trigger, then opens the waitlist modal through it.
   * @param {string} sizeValue
   */
  joinWaitlist(sizeValue) {
    this.close({ restoreFocus: false });

    const openWaitlist = () => {
      const trigger = document.querySelector('[data-testid="add-to-cart"][data-waitlist-trigger]');
      if (trigger instanceof HTMLElement) trigger.click();
    };

    const alreadySelected = this.getSelectedSize()?.trim() === sizeValue.trim();
    if (alreadySelected || !this.mainPicker) {
      openWaitlist();
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 4000);
    document.addEventListener(
      'variant:update',
      () => {
        window.clearTimeout(timeout);
        controller.abort();
        requestAnimationFrame(openWaitlist);
      },
      { signal: controller.signal }
    );
    this.syncSizeWithVariantPicker(sizeValue);
  }

  /** @returns {string | null} */
  getSelectedColor() {
    if (this.selectedColor) return this.selectedColor;

    const sticky = document.querySelector('color-picker-sticky input[name="color-sticky"]:checked');
    if (sticky instanceof HTMLInputElement) return sticky.value;

    const main = this.mainPicker?.querySelector('[data-option-kind="color"] input:checked');
    if (main instanceof HTMLInputElement) return main.value;

    return null;
  }

  /** @param {string} sizeValue */
  syncSizeWithVariantPicker(sizeValue) {
    const value = sizeValue.trim();
    const picker = this.mainPicker;

    if (picker) {
      const radios = picker.querySelectorAll('[data-option-kind="size"] input[type="radio"]');
      const candidates = radios.length ? radios : picker.querySelectorAll('input[type="radio"]');
      for (const radio of candidates) {
        if (radio instanceof HTMLInputElement && radio.value.trim() === value) {
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return true;
        }
      }

      for (const select of picker.querySelectorAll('select')) {
        const option = Array.from(select.options).find((opt) => opt.value.trim() === value);
        if (option) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }

    // Bundle builder / generic size selects
    const selects = document.querySelectorAll(
      'pdp-bundle-builder .pdp-builder__size-select, select[name*="size"], select[name*="Size"], select.size-select, select[data-size]'
    );
    for (const select of selects) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const option = Array.from(select.options).find((opt) => opt.value === value || opt.textContent?.trim() === value);
      if (option) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }

    return false;
  }

  /** @param {CustomEvent} event */
  handleColorSelected = (event) => {
    if (!event.detail?.color) return;
    this.selectedColor = event.detail.color;
    this.updateSizesForColor(this.selectedColor);
  };

  /** @param {string | null} color */
  updateSizesForColor(color) {
    this.querySelectorAll('.size-chart-row[data-size-value]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const colors = (row.dataset.colorOptions || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      const available = color ? colors.includes(color) : Boolean(row.dataset.variantId);
      this.setRowAvailability(row, available);
    });
  }

  /**
   * @param {HTMLElement} row
   * @param {boolean} available
   */
  setRowAvailability(row, available) {
    row.classList.toggle('waitlist', !available);
    row.toggleAttribute('data-waitlist-size', !available);
    let status = row.querySelector('.size-chart-row__status');
    if (!available && !status) {
      status = document.createElement('span');
      status.className = 'size-chart-row__status';
      status.textContent = this.dataset.waitlistLabel || 'Join waitlist';
      row.append(status);
    } else if (available && status) {
      status.remove();
    }
  }
}

if (!customElements.get('size-chart-modal')) {
  customElements.define('size-chart-modal', SizeChartModal);
}

window.SizeChartModal = SizeChartModal;
